/**
 * icons.ts — UI5 icon name → prince-ui IconName adapter
 *
 * Provides `princeIcon(ui5Name)` that maps the UI5 icon identifiers used in
 * this repo to the closest available prince-ui `IconName`.
 *
 * Prince-ui's built-in icon set (v0.10.0, 33 icons):
 *   heart, flame, moon, chart, settings, menu, pin, pin-off, user, building,
 *   search, plus, bolt, alert, inbox, compass, mail, bell, grid, more,
 *   maximize, check, x, chevron-right, chevron-down, chevron-up, phone,
 *   clock, link, arrow-down-right, arrow-up-right, droplet, file-text
 *
 * Coverage legend:
 *   (exact)  — semantic 1:1 match
 *   (approx) — visually/semantically close but not identical; marked TODO
 *   (none)   — no reasonable match; falls back to "grid" as a neutral glyph
 */

import type { IconName } from "@conuti-das/prince-ui";

/** Map from UI5 icon dist basename → prince-ui IconName */
const UI5_TO_PRINCE: Record<string, IconName> = {
  // ── Status / process icons ────────────────────────────────────────────
  "accept":            "check",           // exact (checkmark = accept)
  "decline":           "x",              // exact (X = decline/close)
  "pending":           "clock",           // approx: pending → clock TODO: approx
  "status-completed":  "check",           // exact (completed = check)
  "status-in-process": "bolt",            // approx: in-process → bolt TODO: approx
  "status-error":      "x",              // approx: error → X TODO: approx
  "status-positive":   "check",           // approx: positive → check TODO: approx
  "status-negative":   "x",              // approx: negative → X TODO: approx
  "status-inactive":   "moon",            // approx: inactive → moon TODO: approx
  "error":             "x",              // approx: error → X TODO: approx
  "warning":           "alert",           // exact (triangle warning)
  "information":       "inbox",           // approx: info → inbox TODO: approx
  "alert":             "alert",           // exact

  // ── Navigation / UI chrome ────────────────────────────────────────────
  "home":                    "compass",        // approx: home → compass (navigation metaphor)
  "navigation-right-arrow":  "chevron-right",  // exact (directional)
  "feeder-arrow":            "arrow-up-right", // approx: feeder-arrow → arrow-up-right
  "filter":                  "settings",       // approx: filter → settings (both are "configure view")
  "search":                  "search",         // exact
  "refresh":                 "arrow-up-right", // approx: refresh → arrow (movement)
  "synchronize":             "arrow-up-right", // approx: sync → arrow (data movement)
  "history":                 "clock",          // approx: history → clock (time-based)
  "reset":                   "x",              // approx: reset → X (clear/undo)

  // ── Content / Documents ───────────────────────────────────────────────
  "document":    "file-text",        // exact
  "source-code": "file-text",        // approx: source-code → file-text (both are text files)
  "download":    "arrow-down-right", // approx: download → arrow-down (direction matches)
  "upload":      "arrow-up-right",   // approx: upload → arrow-up (direction matches)
  "save":        "check",            // approx: save → check (confirm action)
  "log":         "file-text",        // approx: log → file-text (log is text-based)
  "delete":      "x",               // approx: delete → X (remove/close semantic)
  "copy":        "plus",             // approx: copy → plus (copy adds a new item)
  "validate":    "check",            // approx: validate → check (pass/OK)
  "chain-link":  "link",             // exact (hyperlink)
  "show":        "maximize",         // approx: show → maximize (reveal/expand)
  "hide":        "pin-off",          // approx: hide → pin-off (un-pin = hide)

  // ── Infrastructure / IT ───────────────────────────────────────────────
  "building":    "building",   // exact
  "cloud":       "inbox",      // approx: cloud → inbox (remote storage metaphor)
  "database":    "inbox",      // approx: database → inbox (storage container)
  "it-system":   "settings",   // approx: system → settings (configurable component)
  "it-instance": "more",       // approx: instance → more (sub-item)
  "it-host":     "more",       // approx: host → more (sub-item)
  "factory":     "building",   // approx: factory → building (physical structure)
  "connected":   "link",       // approx: connected → link (connection metaphor)
  "disconnected":"x",          // approx: disconnected → X (broken/removed)
  "org-chart":   "user",       // approx: org-chart → user (people/org hierarchy)
  "tree":        "chevron-down", // approx: tree → chevron-down (expandable hierarchy)

  // ── Actions / Misc ────────────────────────────────────────────────────
  "add":                "plus",      // exact
  "play":               "bolt",      // approx: play → bolt (action/trigger)
  "process":            "bolt",      // approx: process → bolt (running action)
  "inspect":            "search",    // approx: inspect → search (examine detail)
  "palette":            "droplet",   // approx: palette → droplet (color metaphor)
  "lab":                "flame",     // approx: lab → flame (experimental/chemistry)
  "idea-wall":          "bolt",      // approx: idea → bolt (creative spark)
  "learning-assistant": "user",      // approx: learning → user (assistant = person)
  "sys-help":           "bell",      // approx: help → bell (notification/info)
  "sys-enter-2":        "check",     // approx: sys-enter → check (confirm/enter)
  "sap-ui5":            "settings",  // approx: SAP UI5 logo → settings (framework/config)

  // ── Time / Scheduling ─────────────────────────────────────────────────
  "task":               "check",     // approx: task → check (completed task)
  "time-entry-request": "clock",     // approx: time-entry → clock (time-based)
  "timesheet":          "clock",     // approx: timesheet → clock (time tracking)
};

/**
 * Returns the prince-ui `IconName` closest to the given UI5 icon identifier.
 * The UI5 name may include or omit the `webcomponents-icons/dist/` prefix.
 *
 * Falls back to `"grid"` (neutral glyph) when no mapping exists, so callers
 * never receive undefined.
 *
 * @example
 *   princeIcon("accept")          // → "check"
 *   princeIcon("status-positive") // → "check"
 *   princeIcon("unknown-icon")    // → "grid"  (fallback)
 */
export function princeIcon(ui5Name: string): IconName {
  // Strip optional path prefix
  const name = ui5Name.replace(/^.*\/dist\//, "").replace(/\.js$/, "");
  return UI5_TO_PRINCE[name] ?? "grid";
}

// Export the raw map for inspection / tests
export { UI5_TO_PRINCE };
